import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { IS_CLIENT, IS_PRODUCTION } from '@repo/common-utils';

import { DatadogEventData, DatadogEventParams, SetUserParams } from '../types';

import { BaseService } from './BaseService';

export class DatadogService extends BaseService {
  private readonly datadogConfig?: RumInitConfiguration;
  private readonly appVersion?: string;
  private isInit: boolean = false;

  constructor({ appId, clientId, datadogConfig, appVersion }: { appId: string; clientId: string; datadogConfig?: RumInitConfiguration; appVersion?: string }) {
    super(appId, clientId);
    this.datadogConfig = datadogConfig;
    this.appVersion = appVersion;
    this.init();
  }

  protected init() {
    if (!IS_CLIENT || !this.datadogConfig) return;

    const config: RumInitConfiguration = {
      ...this.datadogConfig,
      site: 'datadoghq.com',
      env: process.env.NEXT_PUBLIC_ZENV ?? 'dev',
      version: this.appVersion,
      trackResources: true,
      trackLongTasks: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 1,
      allowedTracingUrls: [/https:\/\/(.+\.)?api\.bznav\.com/],
      trackSessionAcrossSubdomains: true,
      trackUserInteractions: true,
      useSecureSessionCookie: true,
      usePartitionedCrossSiteSessionCookie: true
    };

    datadogRum.init(config);
    datadogLogs.init({
      clientToken: config.clientToken,
      site: config.site,
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
      service: config.service
    });
    this.isInit = true;
    datadogRum.startSessionReplayRecording();
  }

  setUser({ id = '', name = '', email = '' }: SetUserParams) {
    datadogRum.setUser({
      id: id ?? 'bznav-app-nolec-id',
      email: email ?? 'bznav-app-nolec-email',
      name: name ?? 'bznav-app-nolec-name',
      traffic_type: !IS_PRODUCTION ? 'internal' : 'user'
    });
  }

  sendEvent(params: DatadogEventParams) {
    if (!IS_CLIENT || !this.isInit || params.action !== 'viewed') return;

    const eventNameParams = [params?.category, params?.object, params?.detail, params?.action];
    const event = eventNameParams.filter((value) => !!value).join('_');
    this.sendDatadogEvent({
      type: 'action',
      name: event,
      context: params.attributes
    });
  }

  sendDatadogEvent(data: DatadogEventData) {
    if (!IS_CLIENT || !this.isInit) return;

    switch (data.type) {
      case 'error': {
        datadogRum.addError(data.error, data.context);
        break;
      }
      case 'timing': {
        datadogRum.addTiming(data.name, data.time);
        break;
      }
      case 'durationVital': {
        datadogRum.addDurationVital(data.name, {
          startTime: data.startTime ?? performance.now() - data.duration,
          duration: data.duration,
          description: data.description ?? '',
          context: data.context ?? {}
        });
        break;
      }
      case 'featureFlag': {
        datadogRum.addFeatureFlagEvaluation(data.name, data.value);
        break;
      }
      case 'logger': {
        const logLevel = data.level || 'info';
        datadogLogs.logger[logLevel](data.message, data.messageContext, data.error);
        break;
      }
      case 'action': {
        datadogRum.addAction(data.name, data.context);
        break;
      }
    }
  }

  unsetUser() {
    datadogRum.clearUser();
  }
}
